import { usePostHog } from 'posthog-js/react';
import { useCallback } from 'react';
import { isProduction } from '../environment.ts';

export type EventMapBase = Record<string, object | undefined>;

export type SendEventArgs<
    Events extends EventMapBase,
    Event extends keyof Events,
> = undefined extends Events[Event] ? [event: Event] : [event: Event, payload: Events[Event]];

export type SendEventFn<Events extends EventMapBase> = <Event extends keyof Events & string>(
    ...args: SendEventArgs<Events, Event>
) => void;

export type UseAnalyticsHookFn<Events extends EventMapBase> = () => SendEventFn<Events>;

export function makeUseAnalytics<Events extends EventMapBase>(): UseAnalyticsHookFn<Events> {
    return useAnalytics<Events>;
}

function useAnalytics<Events extends EventMapBase>(): SendEventFn<Events> {
    const postHog = usePostHog();

    return useCallback(
        <Event extends keyof Events & string>(event: Event, payload?: Events[Event]): void => {
            if (!isProduction()) {
                if (payload) {
                    console.groupCollapsed(
                        `%c📊 Event: ${event}%c`,
                        'color: white; background: #3b3b98; padding: 4px 8px; border-radius: 4px;',
                        'color: #f8a5c2; font-weight: bold;',
                    );
                    console.log(
                        `%c ${JSON.stringify(payload, null, 2)}`,
                        'color: #f78fb3; font-weight: bold;',
                    );
                    console.groupEnd();
                } else {
                    console.log(
                        `%c📊 Event: ${event}%c`,
                        'color: white; background: #3b3b98; padding: 4px 8px; border-radius: 4px;',
                        'color: #f8a5c2; font-weight: bold;',
                    );
                }
            }

            postHog.capture(event, payload);
        },
        [postHog],
    );
}
